import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Home } from "lucide-react";
import { Timeline } from "vis-timeline/standalone";
import type { TimelineOptions, DataItem } from "vis-timeline";
import { DataSet } from "vis-data";
import { useEditorContext, type TimelineEvent, type ListItem } from "@/lib/editorContext";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import "vis-timeline/styles/vis-timeline-graph2d.min.css";

type TimeScale = "day" | "week" | "month" | "year";
type ScaleMode = TimeScale | "auto";
type ContextMenuState = { x: number; y: number; itemId: string } | null;

const ZOOM_MIN_MS = 1000 * 60 * 60 * 6; // 6 hours
const ZOOM_MAX_MS = 1000 * 60 * 60 * 24 * 365 * 5; // 5 years
const CONTEXT_MENU_WIDTH = 220;
const CONTEXT_MENU_HEIGHT = 96;
const DAY_MS = 1000 * 60 * 60 * 24;

const countDescendants = (item: ListItem): number =>
  item.children.reduce((acc, child) => acc + 1 + countDescendants(child), 0);

const buildEventFromListItem = (item: ListItem): TimelineEvent | null => {
  const startDate = item.manualStartDate ?? item.extractedStartDate;
  if (!startDate) return null;
  let endDate: Date | null = null;
  if (item.manualStartDate) {
    if (item.manualEndDate && item.manualEndDate.getTime() >= item.manualStartDate.getTime()) {
      endDate = item.manualEndDate;
    }
  } else if (item.extractedEndDate && item.extractedEndDate.getTime() >= startDate.getTime()) {
    endDate = item.extractedEndDate;
  }
  return {
    id: item.id,
    itemId: item.id,
    text: item.text || "Untitled item",
    startDate,
    endDate,
    isManual: Boolean(item.manualStartDate),
    hasChildren: item.children.length > 0,
    childCount: countDescendants(item),
    level: item.level,
    expanded: item.children.length > 0,
    parentId: item.parentId,
  };
};

const pickScaleForSpan = (spanMs: number): TimeScale => {
  if (!Number.isFinite(spanMs) || spanMs <= DAY_MS * 14) {
    return "day";
  }
  if (spanMs <= DAY_MS * 90) {
    return "week";
  }
  if (spanMs <= DAY_MS * 365 * 2) {
    return "month";
  }
  return "year";
};

const SCALE_CONFIG: Record<TimeScale, TimelineOptions> = {
  day: {
    timeAxis: { scale: "day", step: 1 },
  },
  week: {
    timeAxis: { scale: "week", step: 1 },
  },
  month: {
    timeAxis: { scale: "month", step: 1 },
  },
  year: {
    timeAxis: { scale: "year", step: 1 },
  },
};

const TIME_SCALE_LABELS: Record<TimeScale, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
};

const ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#039;",
};

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, char => ESCAPE_MAP[char] ?? char);

const buildTimelineItemContent = (event: TimelineEvent) => {
  const badge = event.childCount > 0 ? `<span class="timeline-badge">📅 ${event.childCount}</span>` : "";
  const indicator = event.hasChildren ? `<span class="timeline-indicator">${event.expanded ? "▾" : "▸"}</span>` : "";
  const indent = Math.min(event.level, 3) * 12;
  return `<div class="timeline-chip" style="padding-left:${indent + 16}px">${indicator}<span class="timeline-chip-text">${escapeHtml(event.text)}</span>${badge}</div>`;
};

export function TimelinePanel() {
  const {
    timelineEvents,
    selectedItemId,
    selectItem,
    openDateEditor,
    toggleExpanded,
    updateManualDate,
    listItemMap,
  } = useEditorContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<Timeline | null>(null);
  const dataSetRef = useRef(new DataSet<DataItem, "id">());
  const extentRef = useRef<{ start: number; end: number; count: number } | null>(null);
  const [scaleMode, setScaleMode] = useState<ScaleMode>("auto");
  const [autoScale, setAutoScale] = useState<TimeScale>("month");
  const [focusStack, setFocusStack] = useState<string[]>([]);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const closeContextMenu = useCallback(() => setContextMenu(null), []);
  const effectiveScale = scaleMode === "auto" ? autoScale : scaleMode;
  const updateAutoScaleForSpan = useCallback((spanMs: number) => {
    if (!Number.isFinite(spanMs) || spanMs <= 0) return;
    const nextScale = pickScaleForSpan(spanMs);
    setAutoScale(prev => (prev === nextScale ? prev : nextScale));
  }, []);
  const syncAutoScaleFromWindow = useCallback(() => {
    if (!timelineRef.current || typeof timelineRef.current.getWindow !== "function") return;
    const windowRange = timelineRef.current.getWindow();
    if (!windowRange) return;
    const span = windowRange.end.valueOf() - windowRange.start.valueOf();
    updateAutoScaleForSpan(Math.abs(span));
  }, [updateAutoScaleForSpan]);
  const focusedParentId = focusStack[focusStack.length - 1] ?? null;

  useEffect(() => {
    setFocusStack(prev => prev.filter(id => listItemMap.has(id)));
  }, [listItemMap]);

  useEffect(() => {
    extentRef.current = null;
  }, [focusedParentId]);

  const handleClick = useCallback(
    (props: any) => {
      closeContextMenu();
      if (!props.item) return;
      const id = String(props.item);
      selectItem(id);
    },
    [closeContextMenu, selectItem]
  );

  const handleDoubleClick = useCallback(
    (props: any) => {
      closeContextMenu();
      if (!props.item) return;
      const id = String(props.item);
      const target = listItemMap.get(id);
      if (!target) return;
      if (target.children.length) {
        setFocusStack(prev => {
          const existingIndex = prev.indexOf(id);
          if (existingIndex >= 0) {
            return prev.slice(0, existingIndex + 1);
          }
          return [...prev, id];
        });
        return;
      }
      toggleExpanded(id);
    },
    [closeContextMenu, listItemMap, toggleExpanded]
  );

  const handleItemMove = useCallback(
    (props: any) => {
      closeContextMenu();
      if (!props.item || !props.start) return;
      const id = String(props.item);
      const target = listItemMap.get(id);
      if (!target) return;
      const nextDate = new Date(props.start);
      if (Number.isNaN(nextDate.getTime())) return;
      updateManualDate(id, nextDate, target.text);
    },
    [closeContextMenu, listItemMap, updateManualDate]
  );

  const handleRangeChanged = useCallback(
    (props: any) => {
      if (scaleMode !== "auto") return;
      const startMs = props?.start ? new Date(props.start).getTime() : NaN;
      const endMs = props?.end ? new Date(props.end).getTime() : NaN;
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return;
      updateAutoScaleForSpan(Math.abs(endMs - startMs));
    },
    [scaleMode, updateAutoScaleForSpan]
  );

  const handleContextMenu = useCallback(
    (props: any) => {
      props.event?.preventDefault();
      const originalEvent: MouseEvent | undefined = props.event;
      if (!props.item || !originalEvent) {
        closeContextMenu();
        return;
      }
      const id = String(props.item);
      selectItem(id);
      const viewportWidth = window.innerWidth ?? 0;
      const viewportHeight = window.innerHeight ?? 0;
      const x = Math.min((originalEvent.clientX ?? 0) + 4, viewportWidth - CONTEXT_MENU_WIDTH);
      const y = Math.min((originalEvent.clientY ?? 0) + 4, viewportHeight - CONTEXT_MENU_HEIGHT);
      setContextMenu({ x: Math.max(x, 8), y: Math.max(y, 8), itemId: id });
    },
    [closeContextMenu, selectItem]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    const options: TimelineOptions = {
      ...SCALE_CONFIG.month,
      height: "100%",
      stack: true,
      showCurrentTime: true,
      moveable: true,
      editable: { updateTime: true },
      multiselect: false,
      horizontalScroll: true,
      zoomable: true,
      zoomMin: ZOOM_MIN_MS,
      zoomMax: ZOOM_MAX_MS,
      orientation: "top",
      minHeight: 220,
      tooltip: { followMouse: true },
    };
    timelineRef.current = new Timeline(containerRef.current, dataSetRef.current, options);
    const current = timelineRef.current;
    current.on("click", handleClick);
    current.on("doubleClick", handleDoubleClick);
    current.on("itemmove", handleItemMove);
    current.on("contextmenu", handleContextMenu);
    current.on("rangechange", handleRangeChanged);
    current.on("rangechanged", handleRangeChanged);

    return () => {
      current.destroy();
      timelineRef.current = null;
    };
  }, [handleClick, handleDoubleClick, handleContextMenu, handleItemMove, handleRangeChanged]);

  useEffect(() => {
    if (!contextMenu) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.(".timeline-context-menu")) {
        return;
      }
      closeContextMenu();
    };
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeContextMenu();
      }
    };
    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("scroll", closeContextMenu, true);
    window.addEventListener("resize", closeContextMenu);
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("scroll", closeContextMenu, true);
      window.removeEventListener("resize", closeContextMenu);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [closeContextMenu, contextMenu]);

  useEffect(() => {
    if (!timelineRef.current) return;
    timelineRef.current.setOptions({ timeAxis: SCALE_CONFIG[effectiveScale].timeAxis });
    timelineRef.current.redraw();
  }, [effectiveScale]);

  useEffect(() => {
    if (scaleMode === "auto") {
      syncAutoScaleFromWindow();
    }
  }, [scaleMode, syncAutoScaleFromWindow]);

  const displayedEvents = useMemo(() => {
    if (!focusedParentId) {
      return timelineEvents;
    }
    const parent = listItemMap.get(focusedParentId);
    if (!parent) return [];
    if (!parent.children.length) return [];
    return parent.children
      .map(child => buildEventFromListItem(child))
      .filter((event): event is TimelineEvent => Boolean(event));
  }, [focusedParentId, listItemMap, timelineEvents]);

  useEffect(() => {
    const dataSet = dataSetRef.current;
    const items: DataItem[] = displayedEvents.map((event: TimelineEvent): DataItem => ({
      id: event.id,
      start: event.startDate,
      end: event.endDate ?? event.startDate,
      type: event.endDate ? "range" : "box",
      content: buildTimelineItemContent(event),
      className: `${event.isManual ? "timeline-manual" : "timeline-auto"}${event.endDate ? " timeline-range" : ""}`,
    }));

    const targetIds = new Set(items.map(item => String(item.id)));
    const existingIds = dataSet.getIds();
    const toRemove = existingIds.filter(id => !targetIds.has(String(id)));
    if (toRemove.length) {
      dataSet.remove(toRemove);
    }
    if (items.length) {
      dataSet.update(items);
    } else {
      dataSet.clear();
    }

    if (timelineRef.current && displayedEvents.length) {
      const bounds = { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY };
      displayedEvents.forEach(event => {
        const start = event.startDate.getTime();
        const end = (event.endDate ?? event.startDate).getTime();
        bounds.min = Math.min(bounds.min, start);
        bounds.max = Math.max(bounds.max, end);
      });

      if (Number.isFinite(bounds.min) && Number.isFinite(bounds.max)) {
        const nextExtent = { start: bounds.min, end: bounds.max, count: displayedEvents.length };
        const prevExtent = extentRef.current;
        const shouldFit =
          !prevExtent ||
          nextExtent.count > prevExtent.count ||
          nextExtent.start < prevExtent.start ||
          nextExtent.end > prevExtent.end;
        extentRef.current = nextExtent;
        if (shouldFit) {
          timelineRef.current.fit({ animation: { duration: 450 } });
        }
      }
    } else {
      extentRef.current = null;
    }

    timelineRef.current?.redraw();
    if (scaleMode === "auto") {
      syncAutoScaleFromWindow();
    }
  }, [displayedEvents, scaleMode, syncAutoScaleFromWindow]);

  useEffect(() => {
    if (!timelineRef.current) return;
    if (selectedItemId) {
      timelineRef.current.setSelection([selectedItemId]);
      // attempt to focus the selected item without animation options
      timelineRef.current.focus(selectedItemId);
    } else {
      timelineRef.current.setSelection([]);
    }
  }, [selectedItemId]);

  const zoomIn = () => timelineRef.current?.zoomIn(0.5);
  const zoomOut = () => timelineRef.current?.zoomOut(0.5);
  const autoFit = () => timelineRef.current?.fit({ animation: { duration: 350 } });
  const goHome = useCallback(() => setFocusStack([]), []);
  const jumpToBreadcrumb = useCallback((index: number) => {
    setFocusStack(prev => prev.slice(0, index + 1));
  }, []);

  const isEmpty = displayedEvents.length === 0;
  const scaleButtons = useMemo(() => Object.keys(TIME_SCALE_LABELS) as TimeScale[], []);

  return (
    <div className="flex h-full w-full min-h-0 flex-1 flex-col gap-3 rounded-2xl p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Breadcrumb>
          <BreadcrumbList className="text-xs">
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <button
                  type="button"
                  onClick={goHome}
                  aria-label="Return to top level"
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium transition cursor-pointer ${
                    focusStack.length === 0
                      ? "border-primary/50 bg-primary/10 text-primary"
                      : "border-border/60 bg-card/80 text-muted-foreground hover:border-border hover:text-card-foreground"
                  }`}
                >
                  <Home className="size-3.5" />
                  <span className="sr-only">Home</span>
                </button>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {focusStack.length === 0 ? (
              <>
                <BreadcrumbSeparator className="text-border" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-muted-foreground">All timeline items</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            ) : (
              focusStack.map((id, index) => {
                const label = listItemMap.get(id)?.text || `Item ${index + 1}`;
                const isLast = index === focusStack.length - 1;
                return (
                  <Fragment key={id}>
                    <BreadcrumbSeparator className="text-border" />
                    <BreadcrumbItem>
                      {isLast ? (
                        <BreadcrumbPage className="text-primary font-medium">{label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <button
                            type="button"
                            onClick={() => jumpToBreadcrumb(index)}
                            title={`Go to ${label}`}
                            className="inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-medium transition cursor-pointer border-border/60 bg-card/80 text-muted-foreground hover:border-border hover:text-card-foreground"
                          >
                            {label}
                          </button>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </Fragment>
                );
              })
            )}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={autoFit}
            className="cursor-pointer rounded-full border border-border/70 px-3 py-1 text-xs font-medium text-muted-foreground transition hover:border-border"
          >
            Fit
          </button>
          <button
            type="button"
            onClick={zoomOut}
            className="cursor-pointer rounded-full border border-border/70 px-3 py-1 text-xs text-muted-foreground transition hover:border-border"
          >
            -
          </button>
          <button
            type="button"
            onClick={zoomIn}
            className="cursor-pointer rounded-full border border-border/70 px-3 py-1 text-xs text-muted-foreground transition hover:border-border"
          >
            +
          </button>
        </div>
      </div>
      <div className="relative flex-1 min-h-0">
        <div className="timeline-shell relative h-full w-full">
          <div ref={containerRef} className="h-full w-full" />
          {isEmpty && (
            <div className="timeline-shell-empty absolute inset-0 grid place-items-center px-6 text-center">
              <p className="text-sm font-medium text-muted-foreground">
                No dates detected yet. Try adding dates to your list items!
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <button
          type="button"
          onClick={() => setScaleMode("auto")}
          className={`cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition ${
            scaleMode === "auto"
              ? "border border-primary bg-primary text-primary-foreground"
              : "border border-border/60 text-muted-foreground"
          }`}
        >
          Auto
        </button>
        {scaleButtons.map(scaleKey => (
          <button
            key={scaleKey}
            type="button"
            onClick={() => setScaleMode(scaleKey)}
            className={`cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition ${
              scaleMode === scaleKey
                ? "border border-primary bg-primary text-primary-foreground"
                : "border border-border/60 text-muted-foreground"
            }`}
          >
            {TIME_SCALE_LABELS[scaleKey]}
          </button>
        ))}
      </div>
      {contextMenu && (
        <div
          className="timeline-context-menu"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          role="menu"
        >
          <button
            type="button"
            onClick={() => {
              openDateEditor(contextMenu.itemId);
              closeContextMenu();
            }}
          >
            Adjust date manually
          </button>
        </div>
      )}
    </div>
  );
}
